import type { ConsentText } from './types';

/**
 * The plot consent in English - the source every translation follows (see types.ts). Plain words a
 * field officer can read aloud. The standards' pages restate, at member level, what each standard
 * asks of a group member or supplier (owner, 2026-09-28: "dont miss any of the requirement of
 * compliance as we need to take consent again"); they are not the standards, and the organisation's
 * certification lead should check them against its group manual and the current versions.
 */
const COMMON_RIGHTS = [
  'To be told what is found on my plot and why, and to answer it before any decision about it.',
  'To raise a concern or complaint, without it affecting how I am treated.',
  'To ask to withdraw my consent at any time; {org} will explain what effect it may have on my participation in this programme.',
];

export const en: ConsentText = {
  selfName: 'English',
  englishName: 'English',
  labels: {
    stageDraft: 'FOR THE FARMER TO READ - NOT YET SIGNED',
    stagePaper: 'FOR SIGNATURE BY THE FARMER',
    stageSigned: 'CONSENT GIVEN',
    watermark: 'DRAFT',
    kindDraft: 'Draft Consent',
    kindPaper: 'Paper Consent',
    kindDigital: 'Digital Consent',
    record: 'Record',
    page: 'Page',
    generated: 'Generated {at}',
    title: 'Consent of the farmer, for their plot',
    plot: 'Plot',
    valid: 'valid',
    validRange: '{from} to {to}',
    membershipOfGroup: 'membership of the farmer producer group',
    farmerHeading: '1. The farmer',
    name: 'Name',
    memberCode: 'Member code',
    fatherOrSpouse: 'Father / spouse',
    gender: 'Gender',
    age: 'Age',
    address: 'Address',
    mobile: 'Mobile',
    email: 'Email',
    consentLanguage: 'Consent language',
    programmesHeading: 'Programmes this consent covers',
    certificateReference: 'Certificate / reference',
    validUntil: 'valid to {date}',
    inScope: 'In {org}\'s scope',
    membership: 'Membership',
    membershipValue: 'The plot as part of the farmer producer group of {org}',
    agreeFarmer: 'I, {name}, confirm that the details above are mine and correct. The pages that follow describe my plot, my information and what I agree to for each programme it is in. They were explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
    plotHeading: '2. The plot',
    village: 'Village',
    location: 'Location',
    centre: 'Centre point (lat, long)',
    notMapped: 'Boundary not mapped yet',
    declaredArea: 'Declared area (land record)',
    mappedArea: 'Mapped area',
    tenure: 'Tenure',
    landDocuments: 'Land documents',
    trees: 'Trees on the plot',
    excision: 'Excision areas (declared)',
    boundaryHeading: 'Boundary (GPS, latitude, longitude)',
    snapshotCaption: 'Boundary on satellite imagery',
    moreCorners: '... {n} more corners, held in the app',
    agreePlot: 'I agree that this plot may be registered, mapped and monitored by {org} for the programmes on page 1.',
    dataHeading: '3. My participation and my information',
    dutiesHeading: 'As a participant in the programmes, I agree to',
    informationHeading: 'The information kept about me',
    collected: 'What is collected',
    why: 'Why',
    whoReceives: 'Who may receive my information',
    howLong: 'How long it is kept',
    myRights: 'My rights',
    toAsk: 'To ask, complain or withdraw',
    dataBoard: 'If not resolved, I may complain to the Data Protection Board of India.',
    agreeData: 'I agree to my information being collected and used for the purposes described in this consent.',
    sharingHeading: '4. How my information may be used and shared',
    withdrawingHeading: 'Withdrawing my consent',
    iAgreeTo: 'I agree to',
    iAmEntitled: 'I am entitled',
    agreeScope: 'I have understood what {scope} asks of me for plot {plot}, and I agree to it.',
    recordHeading: 'Record of consent',
    consent: 'Consent',
    consentValue: 'Plot consent for {scopes}',
    farmer: 'Farmer',
    recordId: 'Record id',
    givenByOtp: 'Consent given by OTP',
    otpRef: 'OTP Ref.',
    documentSha: 'Document SHA-256',
    signatureOf: 'Signature of {name}',
    thumb: 'Thumb',
    witness: 'Witness (name and signature)',
    date: 'Date',
    witnessFull: 'Name and signature of the witness',
    officerFull: 'Name and signature of the field officer',
    paperNote: 'Given on paper: sign or put a thumb impression here and on every page, in front of a witness. The field officer uploads this signed copy.',
    otpSentence: 'Consent of {name} was obtained through OTP sent to {reached} and verified on {day} at {time}{by}.',
    otpBy: ' by {officer}',
    registeredMobile: 'registered mobile',
    registeredEmail: 'registered email',
    via: 'via',
    and: 'and',
    registeredContact: 'the registered contact',
    coversHeading: 'What this consent covers',
    partiesHeading: 'The persons giving this consent with the farmer',
    relationship: 'Relationship',
    termsHeading: 'Terms of the arrangement',
    termsBlank: 'Share of the produce or sale proceeds: ______ %    Paid to: ________________',
    partiesBlank: 'Names to be written in by hand, each with their signature below.',
    partySignature: 'Signature or thumb of {name}',
    bothVersions: 'This document is in English and in the farmer\'s own language. Both are the same consent; the farmer\'s own language is the one it was explained in.',
    partiesNote: 'Each person named signs this page of the paper copy in front of a witness; the farmer gives consent by OTP or signature.',
  },
  kinds: {
    sale: {
      title: 'Consent of the farmer to the harvest and sale of produce from their plot',
      heading: 'Harvest and sale from this plot',
      about: 'This consent covers harvesting on plot {plot} and selling what it produces - timber, poles, pulpwood, fuelwood, bamboo or other produce - through {org}, and how each sale is recorded and paid.',
      agreeLead: 'I agree to',
      obligations: [
        'Harvest only with the internal harvest permit of {org} and any felling and transit permit the law requires, and only the trees and quantities marked.',
        'Declare truthfully the species, number of trees and volume or weight of every harvest, and allow it to be measured, photographed and recorded against this plot.',
        'Sell produce from this plot that carries a certification or deforestation-free claim only through {org} or a buyer it names, and never mix produce from anywhere else with it.',
        'Have each sale recorded with an invoice, a dispatch docket and the transit papers, and confirm the quantity and amount of each sale with my OTP or signature.',
        'Allow {org} to share the plot, harvest and sale details that a buyer, certification body or authority needs to trace the produce to this plot.',
        'Allow agreed deductions - harvesting and transport costs, advances, input loans and group fees - to be taken from the sale amount, as shown on my statement.',
        'Replant, or allow natural regeneration, after harvest as the management plan requires.',
        'Tell {org} at once of any dispute over the trees, the land or the produce, before it is sold.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To know the rate, quantity, deductions and net amount of every sale before I confirm it, and to receive a copy of the invoice.',
        'To be paid the net amount into my own bank account within the time {org} has agreed, and to see every payment on my statement.',
        'To refuse a sale whose rate or quantity I do not accept.',
        'To ask for the records of any sale from my plot.',
      ],
      agree: 'I, {name}, agree to the harvest and sale of produce from plot {plot} on these terms. They were explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to harvest and sale from the plot',
    },
    family: {
      title: 'Consent of the family members for the plot held by the family',
      heading: 'The family members\' consent',
      about: 'Plot {plot} is held jointly by the family, or was inherited and is not yet divided. {name} enrols it with {org}. The family members named below confirm that they know of this and agree to it.',
      agreeLead: 'We, the family members named below, agree that',
      obligations: [
        '{name} may enrol this plot with {org}, have it mapped and monitored, and bring it into the programmes {name} has consented to.',
        '{name} may plan and carry out harvests on this plot, and sell its produce through {org}.',
        'Payment for produce from this plot may be made to {name}; how it is shared within the family is a matter for the family.',
        'We will tell {org} of any partition, sale, mortgage or dispute concerning this plot, and of any change in who holds it.',
        'None of us will harvest or sell produce from this plot separately, or enrol it with anyone else, while this consent lasts.',
        'Our names, our relationship to {name} and our contact numbers may be recorded with this consent.',
      ],
      rightsLead: 'We understand that',
      rights: [
        'This consent does not change who owns the land or anyone\'s share in it.',
        'Any of us may ask to withdraw this consent; {org} will then review whether the plot can stay enrolled.',
        'Each of us may see the plot\'s records, harvests and sales on request, through {name} or {org}.',
      ],
      agree: 'We, the family members named above, and I, {name}, have had this consent explained to us in {languages}, which we understand, and we give it freely.',
      consentValue: 'Family members\' consent for the plot',
    },
    sharecrop: {
      title: 'Consent of the landowner and the sharecropper for the plot',
      heading: 'The landowner\'s and sharecropper\'s consent',
      about: 'Plot {plot} is cultivated under a sharecropping arrangement (bargadar / adhiar). The landowner and the sharecropper named below both agree before the plot is enrolled with {org}, harvested and its produce sold.',
      agreeLead: 'We, the landowner and the sharecropper, agree that',
      obligations: [
        'The plot may be enrolled with {org}, mapped and monitored, and brought into the programmes consented to for it.',
        'The share of the produce, or of the sale proceeds, is as written in the terms below; {org} will show each person\'s share on the sale record.',
        'Harvests are planned and carried out by the sharecropper with the landowner\'s knowledge, under the internal harvest permit of {org} and every permit the law requires.',
        'Produce from the plot is sold through {org}, recorded against this plot, and never mixed with produce from anywhere else.',
        'Payment is made as the terms below say: to each of us our share, or to the person named to receive it.',
        'Either of us will tell {org} at once of any change to the arrangement, any dispute, or the end of the arrangement.',
      ],
      rightsLead: 'We understand that',
      rights: [
        'This consent records the arrangement; it does not change the ownership of the land, or any right the sharecropper has under the law.',
        'Each of us may see the plot\'s harvest and sale records and our own share of each sale.',
        'Either of us may ask to withdraw this consent; {org} will then review whether the plot can stay enrolled.',
      ],
      agree: 'We, the landowner and the sharecropper named above, have had this consent explained to us in {languages}, which we understand, and we give it freely.',
      consentValue: 'Landowner\'s and sharecropper\'s consent for the plot',
    },
    representative: {
      title: 'Consent of the farmer to appoint a representative',
      heading: 'The representative',
      about: '{name} appoints the person named below to act for them with {org}, only in the matters ticked below and only for the plots named.',
      agreeLead: 'I agree that',
      obligations: [
        'My representative acts only in the matters ticked above and only for the plots named; anything else still needs me.',
        'What my representative confirms in these matters binds me as if I had confirmed it myself.',
        '{org} may send my representative the messages and codes for these matters, on their own mobile number.',
        'I will tell {org} at once if my representative should no longer act for me.',
      ],
      rightsLead: 'I understand that',
      rights: [
        'I remain the member and the holder of my plots; appointing a representative does not transfer my land, my produce or my payments.',
        'Payment for my produce is made to my own account unless I authorise otherwise in writing.',
        'I may end this appointment at any time; it ends on the date shown at the latest.',
      ],
      agree: 'I, {name}, appoint my representative on these terms, and my representative accepts them. They were explained to us in {languages}, which we understand, and we give this consent freely.',
      consentValue: 'Appointment of a representative',
      powersHeading: 'Matters the representative may act in',
      powers: {
        acknowledge: 'Acknowledge consents and records on my behalf, with the code sent to their own mobile',
        confirmSale: 'Confirm the quantity and amount of my sales',
        receivePayment: 'Receive the notices of my payments',
      },
      plotsAll: 'All my plots',
      plotsSome: 'Plots: {plots}',
    },
    dataProcessing: {
      title: 'Consent of the farmer to the collection and use of their information',
      heading: 'My information',
      about: '{org} collects and keeps information about {name} to register them as a member and to run the programmes, sales and payments they take part in. This consent covers that information and how it is used.',
      agreeLead: 'I agree that',
      obligations: [
        '{org} may collect my name, family details, address, phone and email, photographs, ID and land document numbers (kept masked), and the details of my plots, harvests, sales and payments.',
        'This information may be used to register me, to verify my identity and my land, to run the programmes I join, and to record and pay for my produce.',
        'It may be shared only as the consents I give for each programme, sale or document allow, and with authorities where the law requires.',
        'I will keep my details up to date and tell {org} when they change.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To see the information held about me, and to have it corrected or completed.',
        'To have it erased once no law or programme needs it.',
        'To withdraw this consent at any time; information already used may be kept where the law or a programme requires.',
        'To name someone to act for me, and to complain to the Data Protection Board of India if my concern is not resolved.',
      ],
      agree: 'I, {name}, agree to my information being collected and used as described. It was explained to me in {languages}, which I understand, and I give this consent freely.',
      consentValue: 'Consent to the collection and use of information',
    },
    trading: {
      title: 'Consent of the farmer to sell their produce through the farmer producer group',
      heading: 'Selling my produce',
      about: '{name} sells produce through {org}. This consent covers how sales are made, recorded, confirmed and paid.',
      agreeLead: 'I agree to',
      obligations: [
        'Sell produce that carries a certification or deforestation-free claim only through {org} or a buyer it names, and never mix produce from anywhere else with it.',
        'Have each sale recorded with an invoice and the dispatch and transit papers, and confirm its quantity and amount with my OTP or signature.',
        'Allow {org} to share the sale, the produce and where it came from with the buyer, certification bodies and authorities, for traceability.',
        'Allow agreed deductions - advances, input loans, transport and group fees - to be taken from the sale amount, as shown on my statement.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To know the rate, quantity, deductions and net amount of each sale before I confirm it, and to receive a copy of the invoice.',
        'To be paid into my own bank account within the time agreed, and to see every payment on my statement.',
        'To refuse a sale whose rate or quantity I do not accept.',
      ],
      agree: 'I, {name}, agree to this. It was explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to sell through the farmer producer group',
    },
    documentShare: {
      title: 'Consent of the farmer to share their documents',
      heading: 'Sharing my documents',
      about: '{org} may need to show documents about {name}, their plots or their produce to a buyer, auditor or authority. This consent covers sharing them through a secure link.',
      agreeLead: 'I agree that',
      obligations: [
        '{org} may share copies of my records and documents - land, certification, harvest, sale and traceability documents - with the person named on each share, for the purpose stated on it.',
        'Each share is sent as a secure link that expires, and may need a code to open.',
        'Identity numbers on shared documents are masked unless the law requires them in full.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To know what was shared, with whom and when.',
        'To ask {org} to stop a share that has not yet been opened.',
        'To withdraw this consent at any time for future shares.',
      ],
      agree: 'I, {name}, agree to this. It was explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to share documents',
    },
    payment: {
      title: 'Consent of the farmer to how they are paid',
      heading: 'My payments',
      about: '{org} pays {name} for their produce and services. This consent covers where payments are made and how they are recorded.',
      agreeLead: 'I agree that',
      obligations: [
        'Payments to me are made to my own bank account on record, unless I authorise another account in writing.',
        'Agreed deductions - advances, input loans, group fees and taxes the law requires - may be taken before payment, and are shown on my statement.',
        'I will tell {org} at once if my bank account changes, and check each payment on my statement.',
        'Payment notices may be sent to my registered mobile, email or WhatsApp.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To a statement showing each amount due, each deduction and each payment.',
        'To question any payment or deduction and have it explained.',
        'To withdraw this consent at any time; payments already made stand.',
      ],
      agree: 'I, {name}, agree to this. It was explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to payments',
    },
    membership: {
      title: 'Consent of the farmer to membership of the farmer producer group',
      heading: 'Membership',
      about: '{name} joins the farmer producer group of {org}. This consent covers what membership means for them.',
      agreeLead: 'As a member, I agree to',
      obligations: [
        'Follow the group\'s rules and the requirements of the programmes I join, and take part in its training, inspections and meetings.',
        'Keep true records, and never pass off produce from anywhere else as my own.',
        'Tell {org} of any change to my land, its ownership or its use.',
      ],
      rightsLead: 'As a member, I am entitled',
      rights: [
        'To take part in the group\'s programmes and sales, and to be told of its decisions that affect me.',
        'To raise a concern or complaint without it affecting how I am treated.',
        'To leave the group; {org} will explain what it means for my programmes and sales.',
      ],
      agree: 'I, {name}, agree to this. It was explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to membership',
    },
    portalAccess: {
      title: 'Consent of the farmer to use the online portal',
      heading: 'Online access',
      about: '{org} gives {name} access to see their own records online, signing in with a code sent to their registered mobile or email.',
      agreeLead: 'I agree to',
      obligations: [
        'Keep the codes sent to me private and never share them with anyone, including the staff of {org}.',
        'Use the portal only for my own records, and tell {org} at once if I think someone else has used my access.',
        'Allow {org} to record when and from where I sign in, to keep my account safe.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To see my records, plots, harvests, sales, payments and consents online.',
        'To have my access stopped at any time on request.',
      ],
      agree: 'I, {name}, agree to this. It was explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to portal access',
    },
    harvestRequest: {
      title: 'Consent of the farmer to a harvest on their plot',
      heading: 'The harvest',
      about: '{name} asks {org} to arrange or permit a harvest on their plot. This consent covers how the harvest is planned, done and recorded.',
      agreeLead: 'I agree to',
      obligations: [
        'Harvest only the trees and quantities marked, under the internal harvest permit of {org} and every permit the law requires.',
        'Allow the harvest to be measured, photographed and recorded against my plot, and the site checked after it.',
        'Replant, or allow natural regeneration, as the management plan requires.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To be told the trees marked and the quantities estimated before the harvest starts.',
        'To see the harvest record and the yield statement for my plot.',
      ],
      agree: 'I, {name}, agree to this. It was explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to harvest',
    },
    eudrClaim: {
      title: 'Consent of the farmer to EUDR and FSC claims on their produce',
      heading: 'Claims on my produce',
      about: '{org} sells produce from the plots of {name} with an EUDR deforestation-free claim or an FSC claim. This consent covers the information those claims need.',
      agreeLead: 'I agree that',
      obligations: [
        'The geolocation of my plots, and my harvest and sale records, may be shared with buyers and authorities in due diligence statements and claim records.',
        'Produce is sold with a claim only while my plots meet the requirements, and I will keep produce from anywhere else apart.',
        'This information may be kept for at least five years, as the regulation requires.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To know what is shared about my plots, and with whom.',
        'To be told if a claim is withdrawn from my produce, and why.',
      ],
      agree: 'I, {name}, agree to this. It was explained to me in {languages}, which I understand, and I give this consent freely. I may ask to withdraw it at any time.',
      consentValue: 'Consent to EUDR and FSC claims',
    },
    withdrawal: {
      title: 'Withdrawal of consent by the farmer',
      heading: 'The withdrawal',
      about: '{name} withdraws their consent from {org} for the plots or the membership shown below.',
      agreeLead: 'I understand that',
      obligations: [
        'From the date it takes effect, the plots shown are no longer enrolled in the programmes, and no new harvest or sale is made from them through {org}.',
        'Information already used or shared may be kept where the law, a certification or a programme requires.',
        'Payments already due to me are made as before.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To be told what the withdrawal means for my sales, payments and programmes.',
        'To give consent again later, as a new consent.',
      ],
      agree: 'I, {name}, withdraw my consent as shown. It was explained to me in {languages}, which I understand, and I do this freely.',
      consentValue: 'Withdrawal of consent',
      plots: 'Plots withdrawn',
      reason: 'Reason',
      effective: 'Takes effect on',
      allPlots: 'Membership and all plots',
    },
    amendment: {
      title: 'Amendment to the consent of the farmer',
      heading: 'What changes',
      about: 'Only the items below change. Every other word of the signed consent stays as it was.',
      agreeLead: 'What stays the same',
      obligations: [
        'The plot, its boundary and its corners, as recorded in the signed consent.',
        'What {org} may do with my information, and with whom it may be shared.',
        'My right to withdraw my consent at any time, free of charge.',
      ],
      rightsLead: 'I am entitled',
      rights: [
        'To have this amendment read to me in a language I understand before I sign it.',
        'To refuse it: my consent then stays exactly as I signed it.',
      ],
      agree: 'I, {name}, confirm that I asked for the changes shown, that they were explained to me in {languages}, and that I agree to them. Everything else in my consent stays as I signed it.',
      consentValue: 'Amendment No. {n} to the consent',
      numbered: 'Amendment No. {n}',
      originalHeading: 'The consent this amends',
      consentAmended: 'Consent amended',
      givenOn: 'Given on',
      askedFor: 'Amendment asked for on',
      reason: 'Reason',
      item: 'Item', was: 'Was', now: 'Now',
      changeItems: { mobile: 'Mobile', email: 'Email', address: 'Address', valid_to: 'Valid to', scope_codes: 'Programmes this consent covers', rights_holder: 'Rights holder named', other: 'Other' },
    },
  },
  genders: { female: 'Female', male: 'Male', other: 'Other', undisclosed: 'Prefer not to say' },
  commonRights: COMMON_RIGHTS,
  scopes: {
    FSC_FM: {
      title: 'FSC Forest Management (group certification)',
      about: 'The Forest Stewardship Council certifies that trees are grown and harvested responsibly. My plot is certified as a member of the FSC group of {org}.',
      obligations: [
        'Obey the law on my land: land rights, tree felling and transport permits, taxes, and the laws that protect workers, wildlife and water.',
        'Follow the farmer producer group\'s management plan and rules for my plot; keep the records asked of me; tell the farmer producer group of any change to my land or its use.',
        'Fell only with the farmer producer group\'s internal harvest permit and any Forest Department permit needed, only the trees marked, and replant or allow regeneration after harvest.',
        'Keep buffers along streams and water bodies, and around high conservation value, sacred and burial sites; protect rare and threatened plants and animals, and do not hunt them.',
        'Do not clear natural forest or convert it to plantation or other use, and plant no genetically modified trees.',
        'Use no chemical on the FSC list of highly hazardous pesticides; use others only when needed, with protective equipment, safe storage and safe disposal of containers; never burn residue.',
        'Employ no child below the legal working age, use no forced labour, discriminate against no one, respect workers\' right to organise, pay at least the minimum wage and keep workers safe with protective equipment and first aid.',
        'Respect the customary rights of neighbours and communities to paths, water and forest produce, and settle disputes through the farmer producer group\'s process.',
        'Allow the farmer producer group, the certification body, FSC and its accreditation service (ASI) onto my plot, and let them see my records.',
        'Address any observations or corrective actions identified by {org}, the certification body or a verifier within the agreed time. I understand that unresolved issues may affect the inclusion of my plot or produce in the relevant programme or claim.',
        'Sell FSC-claimed produce only through the farmer producer group, with its certificate code on the invoice, and use the FSC name or logo only as the farmer producer group allows.',
      ],
      rights: ['To sell my produce with the FSC claim through the farmer producer group, while my plot meets the rules.', 'To see the farmer producer group\'s rules and my plot\'s management plan.'],
    },
    FSC_CW: {
      title: 'FSC Controlled Wood (verification)',
      about: 'Wood that is not FSC certified can still be sold as FSC Controlled Wood when it is verified to come from acceptable sources. My plot is verified for that.',
      obligations: [
        'The wood from my plot is legally harvested and transported, with every permit and tax the law requires.',
        'It does not come from land where the traditional, civil or human rights of neighbours, communities or workers are not respected.',
        'It does not come from areas whose high conservation values are threatened by the harvest.',
        'It does not come from natural forest being converted to plantation or to another use.',
        'It does not come from trees that are genetically modified.',
        'Give true information on where each lot of wood came from - the plot, the species and the quantity - so it can be traced.',
        'Allow {org} and its verifiers to visit my plot and to see my land, permit and harvest documents, and tell them of any change.',
      ],
      rights: ['To sell my wood as FSC Controlled Wood through {org} while the verification holds.'],
    },
    EUDR: {
      title: 'EU Deforestation Regulation (EUDR)',
      about: 'Wood and other products sold into the European Union must be proven free of deforestation after 31 December 2020 and legally produced. {org} files a due diligence statement that includes my plot.',
      obligations: [
        'Allow my plot\'s boundary (its geolocation) to be recorded, and shared with buyers and EU authorities in due diligence statements.',
        'Keep my plot free of deforestation and forest degradation after 31 December 2020.',
        'Produce legally under the laws of India: land use rights, environmental and forest rules, the rights of others to the land, labour and human rights, free prior and informed consent where it applies, tax, anti-corruption, trade and customs.',
        'Give true information on my land, my harvests and where my produce goes, and keep my produce from this plot apart from produce from anywhere else.',
        'Tell {org} at once of any change to the plot\'s boundary, use or ownership, and of any clearing on it.',
        'Allow {org} to keep this information for at least five years, as the regulation requires, and to show it to the authorities.',
      ],
      rights: ['To know what is shared about my plot, and with whom.'],
    },
    GLOBALGAP: {
      title: 'GLOBALG.A.P. (good agricultural practice)',
      about: 'GLOBALG.A.P. certifies safe and sustainable farming for food and plants sold to retailers. My plot is in the group certificate of {org}.',
      obligations: [
        'Follow the farmer producer group\'s quality management system and rules, and take part in its internal inspections each year.',
        'Keep records of what I plant, the inputs I apply and when, and my harvests, so every lot can be traced to my plot; keep certified and other produce apart.',
        'Use only plant protection products approved for the crop, at the right dose, applied by a trained person with protective equipment, and respect the waiting period before harvest; store them locked and dispose of containers safely.',
        'Record fertiliser use; do not use human sewage sludge; use water that is safe for its purpose.',
        'Keep produce, harvest tools and containers clean, with hand washing and toilets for pickers.',
        'Keep workers safe, trained and informed, with first aid, clean drinking water and fair treatment.',
        'Handle waste and pollution properly; protect soil, water and wildlife on and around my plot.',
        'Tell the farmer producer group at once of any complaint, contamination or food safety problem, and take part in a withdrawal or recall if one is needed.',
        'Allow the farmer producer group\'s inspectors and the certification body to inspect my plot and records, and to take samples for residue testing.',
      ],
      rights: ['To sell my produce as GLOBALG.A.P. certified while my plot meets the rules.'],
    },
    PEFC: {
      title: 'PEFC (sustainable forest management)',
      about: 'PEFC certifies sustainably managed forests and tree farms. My plot is certified as part of the farmer producer group of {org}.',
      obligations: [
        'Obey the law on my land, including forest, environmental, labour and tax law, and hold the permits felling and transport need.',
        'Manage my trees to the farmer producer group\'s plan: keep them productive and healthy, harvest no more than grows back, and replant or allow regeneration.',
        'Do not convert forest to other land use, and plant no genetically modified trees.',
        'Protect soil and water, keep buffers along streams, and protect biodiversity and ecologically important areas on my plot.',
        'Use no pesticide classified as extremely or highly hazardous; use chemicals only when needed, safely and as the label says.',
        'Respect workers\' fundamental rights - no child or forced labour, no discrimination, freedom to organise - and keep them safe.',
        'Respect the rights of local communities, and the cultural and spiritual sites on or near my plot.',
        'Keep the records asked of me, allow the farmer producer group\'s and the certification body\'s audits, and address any corrective actions they identify within the agreed time.',
        'Sell PEFC-claimed wood only through the farmer producer group, and use the PEFC name or logo only as the farmer producer group allows.',
      ],
      rights: ['To sell my wood with the PEFC claim through the farmer producer group while my plot meets the rules.'],
    },
    ORGANIC: {
      title: 'Organic (NPOP / NOP)',
      about: 'Organic certification shows that produce is grown without synthetic fertilisers and pesticides. My plot is in the organic grower group of {org}, under its internal control system.',
      obligations: [
        'Complete the conversion period before my produce is sold as organic.',
        'Use no synthetic fertiliser, pesticide, herbicide or growth regulator, no genetically modified seed or planting material, and no sewage sludge.',
        'Use only inputs the organic standard allows, and keep their bills and labels.',
        'Keep buffers from neighbouring conventional fields, and prevent drift and contamination from them.',
        'If I also grow conventional crops, keep them clearly apart, and never mix conventional produce with organic.',
        'Keep records of my inputs, harvests and sales; use clean, separate bags and stores for organic produce.',
        'Follow the internal control system\'s rules, attend its training, and allow its inspections and sample testing; I understand that a requirement not met may affect whether my produce can be sold as organic.',
        'Sell produce as organic only through the farmer producer group, and label it only as the farmer producer group allows.',
        'Tell the farmer producer group at once of any use of a prohibited substance, contamination, or change to my land.',
      ],
      rights: ['To sell my produce as organic through the farmer producer group while my plot meets the rules.'],
    },
    CARBON: {
      title: 'Carbon project',
      about: 'The trees on my plot store carbon. {org} includes my plot in a carbon project that measures this and may sell carbon credits for it.',
      obligations: [
        'Assign to the project, for its crediting period, the right to the carbon credits from the project trees on my plot, as the project agreement sets out.',
        'Keep the project trees standing for the project period; tell {org} before felling any of them, and replace those that die.',
        'Not enrol the same trees or land in any other carbon project or scheme, and not sell their carbon to anyone else.',
        'Not move the clearing or cutting I would have done here to other land (leakage).',
        'Allow the project to measure the trees, map my plot, and monitor it by satellite, and to share those figures with the registry, the verifier and buyers of the credits.',
        'Tell {org} of any fire, damage, dispute or change of ownership affecting the project trees.',
      ],
      rights: ['To the share of carbon revenue the project agreement sets out, and to see how it is worked out.', 'To free, prior and informed consent to the project, given by this document.'],
    },
  },
  membershipDuties: [
    'Follow the programme requirements for my plot, and tell {org} of any change to my land, its ownership or its use.',
    'Take part in the training, internal inspections and audits, and address any corrective actions within the agreed time.',
    'I understand that each programme has its own requirements. {org} may review my plot and records to determine whether my plot and produce continue to meet the applicable programme requirements.',
    'If any requirement is not followed, I understand that {org} may review whether my plot or produce can continue to be included in the relevant programme or claim.',
    'Keep true records, and never pass off produce from anywhere else as coming from my plot.',
  ],
  dataNotice: {
    collected: 'My name and family details, address, phone and email, ID and land document numbers (kept masked), photographs, my plot\'s boundary and location, the trees and practices on it, and my harvests, sales and payments.',
    purposes: 'To register me and my plot, to certify it under the programmes named in this document, to trace and sell my produce with its claims, to file due diligence statements, to measure carbon, and to pay me.',
    sharedWith: 'Where relevant: certification bodies, auditors, programme or standard owners, verifiers, registries, buyers, supply-chain partners, project partners and government authorities. What buyers and supply-chain partners receive is limited to what is reasonably needed for traceability, verification, due diligence, certification or sale of the produce. My personal information will not be sold for unrelated marketing purposes.',
    retention: 'For as long as this consent lasts, and after it for as long as the law or a programme requires (at least five years for EUDR records).',
    rights: 'To see the data held about me, to have it corrected or completed, to have it erased once no law or programme needs it, to ask to withdraw this consent at any time, and to name someone to act for me.',
  },
  sharing: {
    intro: 'I agree that {org} may collect, record, use and maintain information about me and my plot for the purposes described in this consent. I authorise {org} to share relevant information about me, my plot, production, harvests and transactions with:',
    recipients: [
      'certification bodies and auditors;',
      'programme owners, standard owners and verification organisations;',
      'government and regulatory authorities, where required;',
      'buyers and supply-chain partners, where the information is needed for traceability, claim verification, due diligence or transaction requirements;',
      'carbon project registries, verifiers and relevant project partners, where applicable; and',
      'service providers supporting mapping, monitoring, data management or verification.',
    ],
    limits: [
      'Only information relevant to the applicable programme, claim, verification, traceability or transaction will be shared.',
      '{org} will not sell my personal information for unrelated marketing purposes.',
      'I understand that some information about my plot, such as its location, production, harvest and traceability information, may need to be shared to demonstrate the origin and compliance of the produce.',
    ],
    statements: [
      'I agree that relevant information about my plot and produce may be shared with buyers and supply-chain partners when required to demonstrate origin, traceability, certification claims, due diligence or compliance requirements.',
      'I agree that photographs, plot maps, production information, harvest information and related records may be shared with authorised programme, certification, verification and project organisations for the purposes covered by this consent.',
    ],
    withdrawal: 'I may ask to withdraw my consent at any time. {org} will explain what effect the withdrawal may have on my participation in the relevant programme, certification, claim or project. Information already used or shared before withdrawal may continue to be retained where required for records, legal, certification, traceability or programme requirements.',
  },
};
